import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { shellKeys } from '@app/api/hooks/shell';
import { get, post, withPath } from '@app/api/typed';
import type { ApiData, ApiQuery } from '@app/api/typed';
import type { ApprovalDetail, ApprovalSummary } from './ApprovalsModel';

export type ApprovalsQuery = NonNullable<ApiQuery<'/api/v1/approvals', 'get'>>;
export type ApprovalsPage = ApiData<'/api/v1/approvals'>;
export type ApproveResult = ApiData<'/api/v1/approvals/{id}/approve', 'post'>;
export type RejectResult = ApiData<'/api/v1/approvals/{id}/reject', 'post'>;

export const approvalsKeys = {
  todas: ['approvals'] as const,
  lista: (params: ApprovalsQuery) => ['approvals', 'lista', params] as const,
  resumo: ['approvals', 'resumo'] as const,
  detalhe: (id: string) => ['approvals', 'detalhe', id] as const,
};

/**
 * `status` é `style: form, explode: false` no contrato — vai como
 * `status=aprovada,rejeitada`, não como `status[]=…`, que é o padrão do axios.
 */
function forQueryString(params: ApprovalsQuery): ApprovalsQuery {
  const { status, ...resto } = params;
  return status?.length
    ? ({ ...resto, status: status.join(',') } as unknown as ApprovalsQuery)
    : (resto as ApprovalsQuery);
}

/** Fila (Pendentes ou Histórico) — o filtro inteiro vai para o servidor (FR-057, FR-068). */
export function useApprovals(params: ApprovalsQuery): UseQueryResult<ApprovalsPage, ApiError> {
  return useQuery<ApprovalsPage, ApiError>({
    queryKey: approvalsKeys.lista(params),
    queryFn: () => get('/api/v1/approvals', { params: forQueryString(params) }),
    placeholderData: (previous) => previous,
  });
}

/** Indicadores do topo de T9. */
export function useApprovalsSummary(): UseQueryResult<ApprovalSummary, ApiError> {
  return useQuery<ApprovalSummary, ApiError>({
    queryKey: approvalsKeys.resumo,
    queryFn: () => get('/api/v1/approvals/summary'),
  });
}

/** Detalhe do drawer T10. `id` nulo = drawer fechado, nada é buscado. */
export function useApproval(id: string | null): UseQueryResult<ApprovalDetail, ApiError> {
  return useQuery<ApprovalDetail, ApiError>({
    queryKey: approvalsKeys.detalhe(id ?? ''),
    queryFn: () =>
      get(withPath('/api/v1/approvals/{id}', { id: id ?? '' }) as '/api/v1/approvals/{id}'),
    enabled: Boolean(id),
  });
}

/**
 * Depois de qualquer decisão a fila inteira muda: a pendência sai, o Histórico
 * ganha uma linha, os contadores caem e o contador do menu (`getShell`) também.
 */
function useInvalidarApprovals() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: approvalsKeys.todas });
    void queryClient.invalidateQueries({ queryKey: shellKeys.shell });
  };
}

export interface DecisionVars {
  id: string;
  motivo?: string;
  /** `X-Test-Scenario` — só o mock e o ambiente de teste o respeitam. */
  cenario?: string;
}

/**
 * Aprovar e rejeitar levam a `version` da pendência que a pessoa leu (aim-x53.1, A5).
 * O recálculo a sobe — um anúncio da esteira que troca a declaração, ou outra pendência
 * da identidade aprovada —, e a decisão sobre uma versão velha volta
 * `409 versao_antiga`: a pessoa decidiria sobre o que já não está na fila.
 */
export interface VersionedDecisionVars extends DecisionVars {
  version: number;
}

/** A pendência mudou depois de aberta: a tela relê a fila e o detalhe para mostrar a nova. */
export function isStaleVersion(error: ApiError | null | undefined): boolean {
  return error?.code === 'versao_antiga';
}

/**
 * **Aprovar** (FR-062, FR-063). Grava o contrato e materializa na mesma operação:
 * qualquer não-2xx significa que **nada foi gravado** e a release continua
 * pendente — quem trata a mensagem é `ErrorBanner` com `escrita`.
 */
export function useApproveApproval(): UseMutationResult<
  ApproveResult,
  ApiError,
  VersionedDecisionVars
> {
  const invalidar = useInvalidarApprovals();
  return useMutation<ApproveResult, ApiError, VersionedDecisionVars>({
    mutationFn: ({ id, motivo, cenario, version }) =>
      post(withPath('/api/v1/approvals/{id}/approve', { id }) as '/api/v1/approvals/{id}/approve', {
        body: { reason: motivo?.trim() ? motivo.trim() : null, version },
        ...(cenario ? { config: { headers: { 'X-Test-Scenario': cenario } } } : {}),
      }),
    onSuccess: invalidar,
    onError: (error) => {
      if (isStaleVersion(error)) invalidar();
    },
  });
}

/** **Rejeitar** (FR-062, FR-064) — motivo obrigatório; não chama o executor. */
export function useRejectApproval(): UseMutationResult<
  RejectResult,
  ApiError,
  VersionedDecisionVars
> {
  const invalidar = useInvalidarApprovals();
  return useMutation<RejectResult, ApiError, VersionedDecisionVars>({
    mutationFn: ({ id, motivo, version }) =>
      post(withPath('/api/v1/approvals/{id}/reject', { id }) as '/api/v1/approvals/{id}/reject', {
        body: { reason: motivo ?? '', version },
      }),
    onSuccess: invalidar,
    onError: (error) => {
      if (isStaleVersion(error)) invalidar();
    },
  });
}

/** **Tentar de novo** de uma auto-aprovação que falhou no executor (FR-067). */
export function useRetryAutoApproval(): UseMutationResult<ApproveResult, ApiError, DecisionVars> {
  const invalidar = useInvalidarApprovals();
  return useMutation<ApproveResult, ApiError, DecisionVars>({
    mutationFn: ({ id, cenario }) =>
      post(withPath('/api/v1/approvals/{id}/retry', { id }) as '/api/v1/approvals/{id}/retry', {
        ...(cenario ? { config: { headers: { 'X-Test-Scenario': cenario } } } : {}),
      }),
    onSuccess: invalidar,
  });
}
